import type { ClientAccess, Permission } from '@/features/auth/permissions';
import { api, ApiError, type ProblemDetails } from '@/lib/api';

export type StaffState = 'Invited' | 'Active' | 'Deactivated' | 'ClosedGroup';
export type StaffRole = 'Admin' | 'FullAccess' | 'LimitedAccess';

export interface StaffCapabilities {
    relationshipManager: boolean;
    mobileAppLogin: boolean;
    addFunds: boolean;
    cancelOrders: boolean;
    processReturns: boolean;
}

export interface StaffAccount {
    id: number;
    firstName: string;
    middleName: string;
    lastName: string;
    displayName: string;
    email: string;
    phone: string | null;
    designationId: number | null;
    designation: string | null;
    /** Null for a retired legacy role the admin cannot grant. */
    role: StaffRole | null;
    capabilities: StaffCapabilities;
    /** Every client, or only the ones they are relationship manager of (ALS-95). */
    clientAccess: ClientAccess;
    state: StaffState;
    /** Set while sign-in is refused after too many wrong passwords; null once it ran out or was lifted. */
    lockedUntil: string | null;
    clientsManaged: number;
    lastSignInOn: string | null;
    createdOn: string;
    createdBy: string | null;
    revisedOn: string | null;
    revisedBy: string | null;
}

export interface StaffAccountInput {
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string | null;
    designationId: number | null;
    role: StaffRole;
    capabilities: StaffCapabilities;
    clientAccess: ClientAccess;
    reassignClientsTo?: number | null;
}

/** A role and what it allows, read from the policy the server enforces. */
export interface StaffRoleInfo {
    role: StaffRole;
    permissions: Permission[];
}

export interface NamedOption {
    id: number;
    name: string;
}

export interface ManagedClient {
    id: number;
    name: string;
}

export interface StaffChange {
    id: number;
    action: string;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    changedOn: string;
    changedBy: string | null;
}

/** The 409 the API answers when an action would leave clients without a relationship manager. */
export interface ClientsProblem extends ProblemDetails {
    clients?: ManagedClient[];
    clientCount?: number;
}

export function clientsInTheWay(error: unknown): ManagedClient[] | null {
    if (!(error instanceof ApiError) || error.status !== 409) return null;
    const problem: ClientsProblem = error.problem;
    return Array.isArray(problem.clients) ? problem.clients : null;
}

export const usersApi = {
    list: (q?: string) =>
        api<StaffAccount[]>(`/api/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    get: (id: number) => api<StaffAccount>(`/api/users/${id}`),
    create: (input: StaffAccountInput) =>
        api<StaffAccount>('/api/users', { method: 'POST', body: input }),
    update: (id: number, input: StaffAccountInput) =>
        api<StaffAccount>(`/api/users/${id}`, { method: 'PUT', body: input }),
    deactivate: (id: number, reassignClientsTo?: number | null) =>
        api<StaffAccount>(`/api/users/${id}/deactivate`, {
            method: 'POST',
            body: { reassignClientsTo: reassignClientsTo ?? null },
        }),
    reactivate: (id: number) =>
        api<StaffAccount>(`/api/users/${id}/reactivate`, { method: 'POST' }),
    invite: (id: number) => api(`/api/users/${id}/invite`, { method: 'POST' }),
    unlock: (id: number) => api<StaffAccount>(`/api/users/${id}/unlock`, { method: 'POST' }),
    changes: (id: number) => api<StaffChange[]>(`/api/users/${id}/changes`),
    relationshipManagers: () => api<NamedOption[]>('/api/users/relationship-managers'),
    designations: () => api<NamedOption[]>('/api/users/designations'),
    roles: () => api<StaffRoleInfo[]>('/api/users/roles'),
};
